/**
 * Note Editor Component - Simplified Version
 * User-friendly interface for clinical note creation with AI assistance
 */

import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Autocomplete,
  Switch,
  FormControlLabel,
  Alert,
  IconButton,
  CircularProgress,
  Divider,
  Fade,
  Paper,
  Stack,
  Tooltip,
  keyframes,
  Tabs,
  Tab,
} from '@mui/material'
import {
  Close as CloseIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Mic as MicIcon,
  AutoAwesome as AutoAwesomeIcon,
  Lock as LockIcon,
  Description as DescriptionIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import { Note, NoteType, NoteStatus, Session } from '@/types/contracts'
import {
  saveNote,
  updateNote,
  finalizeNote,
  generateSOAPNote,
} from '@/services/mockContractsApi'
import {
  notesApi,
  NoteCategory,
  TranscribeResponse,
  GenerateNoteResponse,
} from '@/services/notesApi'
import { servicePackageService } from '@/services/servicePackageService'
import DeleteIcon from '@mui/icons-material/Delete'
import SignatureCanvas from 'react-signature-canvas'
import { htmlToMarkdown, markdownToHtml } from '@/utils/markdown'
import { NoteDemographicsHeader } from './NoteDemographicsHeader'

interface NoteEditorProps {
  open: boolean
  onClose: () => void
  sessionData?: any[]
  session: Session
  contract?: {
    id?: string
    patient: {
      id?: string
      name: string
    }
    therapist: {
      name: string
      credentials?: string
    }
  }
  existingNote?: any
  onSuccess: (note: Note) => void
  readOnly?: boolean // For cancelled contracts
  initialNoteCategory?: string // Optional initial category for new notes
}

// Pulse animation for recording
const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
`

const NoteEditor: React.FC<NoteEditorProps> = ({
  open,
  onClose,
  session,
  contract,
  existingNote,
  onSuccess,
  readOnly: forceReadOnly = false,
  initialNoteCategory,
  // sessionData,
}) => {

  console.log("contract", contract)
  const [noteCategories, setNoteCategories] = useState<NoteCategory[]>([])
  const [selectedNoteCategory, setSelectedNoteCategory] = useState<string>('')
  const [noteContent, setNoteContent] = useState(() =>
    htmlToMarkdown(existingNote?.content || '')
  )
  const [cptCodes, setCptCodes] = useState<string[]>(
    existingNote?.cptCodes || []
  )
  const [selectedIcdCodes, setSelectedIcdCodes] = useState<
    Array<{ id: string; code: string; description: string }>
  >([])
  const [selectedCptCodes, setSelectedCptCodes] = useState<
    Array<{ id: string; code: string; description: string }>
  >([])
  const [selectedHcpcsCodes, setSelectedHcpcsCodes] = useState<
    Array<{ id: string; code: string; description: string }>
  >([])
  const [patientVisible, setPatientVisible] = useState(
    existingNote?.visibleto_patient ?? true
  )
  const [savingDraft, setSavingDraft] = useState(false)
  const [finalizing, setFinalizing] = useState(false)
  const [error, setError] = useState('')
  const [cptOptions, setCptOptions] = useState<
    Array<{ id: string; code: string; description: string }>
  >([])
  const [hcpcsOptions, setHcpcsOptions] = useState<
    Array<{ id: string; code: string; description: string }>
  >([])
  const [icdOptions, setIcdOptions] = useState<
    Array<{ id: string; code: string; description: string }>
  >([])
  const [icdInputValue, setIcdInputValue] = useState('')
  const [cptInputValue, setCptInputValue] = useState('')
  const [hcpcsInputValue, setHcpcsInputValue] = useState('')
  const [icdSearch, setIcdSearch] = useState('')
  const [cptSearch, setCptSearch] = useState('')
  const [loadingIcd, setLoadingIcd] = useState(false)
  const [loadingCpt, setLoadingCpt] = useState(false)
  const [loadingHcpcs, setLoadingHcpcs] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en')
  const [isRefining, setIsRefining] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [loadingNote, setLoadingNote] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [signatureData, setSignatureData] = useState<string | null>(
    existingNote?.signature || null
  )
  const [noteDetails, setNoteDetails] = useState<any>({})
  const [hasSavedSignature, setHasSavedSignature] = useState<boolean>(
    !!existingNote?.signature
  )
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(
    existingNote?.pdf_url || (existingNote as any)?.pdfUrl
  )
  const noteInputRef = useRef<HTMLDivElement>(null)
  const signaturePadRef = useRef<SignatureCanvas>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const dialogContentRef = useRef<HTMLDivElement>(null)
  const isUserEditingRef = useRef(false)
  const renderedNoteHtml = useMemo(() => {
    // Auto-format lines ending with colons as subheadings (bold + 16px)
    // This helps format AI-generated lines like "Patient Information:"
    const enhancedContent = noteContent.replace(
      /^([^#*\n\s][^#*\n]*:)$/gm,
      '**$1**'
    )
    return markdownToHtml(enhancedContent)
  }, [noteContent])

  const selectedCategoryTitle = useMemo(() => {
    if (!selectedNoteCategory) return ''
    const category = noteCategories.find(
      cat => String(cat._id) === String(selectedNoteCategory)
    )
    return category ? category.title : selectedNoteCategory
  }, [noteCategories, selectedNoteCategory])

  // Update contentEditable div when rendered HTML changes (but not when user is editing)
  useEffect(() => {
    if (!noteInputRef.current || isUserEditingRef.current || loadingNote) return

    // Only update if content has actually changed
    const currentContent = noteInputRef.current.innerHTML.trim()
    const newContent = renderedNoteHtml || ''

    if (currentContent !== newContent.trim()) {
      // Save cursor position if possible
      const selection = window.getSelection()
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null
      const offset = range?.startOffset || 0

      noteInputRef.current.innerHTML = newContent

      // Try to restore cursor position
      if (range && noteInputRef.current.firstChild) {
        try {
          const newRange = document.createRange()
          newRange.setStart(
            noteInputRef.current.firstChild,
            Math.min(
              offset,
              noteInputRef.current.firstChild.textContent?.length || 0
            )
          )
          newRange.collapse(true)
          selection?.removeAllRanges()
          selection?.addRange(newRange)
        } catch (e) {
          // Ignore cursor restoration errors
        }
      }
    }
  }, [renderedNoteHtml, loadingNote])

  // Populate contentEditable when note finishes loading
  useEffect(() => {
    if (
      !loadingNote &&
      noteContent &&
      noteInputRef.current &&
      !isUserEditingRef.current
    ) {
      const currentContent = noteInputRef.current.innerHTML.trim()
      const expectedContent = renderedNoteHtml || ''
      if (currentContent !== expectedContent.trim()) {
        noteInputRef.current.innerHTML = expectedContent
      }
    }
  }, [loadingNote, noteContent, renderedNoteHtml])

  const isFinalized =
    existingNote?.status === 'finalized' ||
    String(existingNote?.status || '').toLowerCase() === 'finalized'
  const isSessionCompleted = session.status === 'completed'
  const isReadOnly = forceReadOnly || isFinalized
  const isNewNote = !existingNote
  const noteId = existingNote?._id || existingNote?.id
  const hasAgencyId =
    (session as any).agencyId ||
    (session as any).agency_id ||
    (session as any).contract_id?.agencyId ||
    (session as any).contract_id?.agency_id ||
    (session as any).contractId?.agencyId ||
    (session as any).contractId?.agency_id ||
    (contract as any)?.agencyId ||
    (contract as any)?.agency_id;


    console.log("hasAgencyId", hasAgencyId);
  // Fetch note categories on mount
  useEffect(() => {
    const fetchNoteCategories = async () => {
      if (!open) return
      setLoadingCategories(true)
      try {
        const categories = await notesApi.getNoteCategories()
        const currentCategoryId = existingNote?.noteCategory?._id || existingNote?.noteCategory
        const activeCategories = categories.filter(
          cat => cat.is_active || cat._id === currentCategoryId
        )
        setNoteCategories(activeCategories)
        // Set default category if none selected and we have categories
        // Use functional update to avoid stale closure issues
        setSelectedNoteCategory(prev => {
          if (!prev && activeCategories.length > 0) {
            // First check if we can map from existingNote (legacy type)
            // since this runs when categories are loaded
            if (existingNote?.type && !existingNote?.noteCategory) {
              const legacyType = String(existingNote.type).toLowerCase()
              const matchingCategory = activeCategories.find(cat =>
                cat.title.toLowerCase().includes(legacyType) ||
                cat.type?.toLowerCase().includes(legacyType)
              )
              if (matchingCategory) return matchingCategory._id
            }
            return activeCategories[0]._id
          }
          return prev
        })
      } catch (err: any) {
        setError(err.message || 'Failed to load note categories')
      } finally {
        setLoadingCategories(false)
      }
    }
    fetchNoteCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Fetch ICD codes when search term changes
  useEffect(() => {
    let isMounted = true
    if (!open) return

    const handler = window.setTimeout(async () => {
      setLoadingIcd(true)
      try {
        const result = await servicePackageService.fetchIcdCodes(
          icdSearch || ''
        )
        if (isMounted) {
          setIcdOptions(result)
        }
      } catch (error) {
        console.error('Failed to load ICD codes', error)
        if (isMounted) {
          setIcdOptions([])
        }
      } finally {
        if (isMounted) {
          setLoadingIcd(false)
        }
      }
    }, 300)

    return () => {
      isMounted = false
      window.clearTimeout(handler)
    }
  }, [icdSearch, open])

  // Fetch CPT codes when search term changes
  useEffect(() => {
    let isMounted = true
    if (!open) return

    const handler = window.setTimeout(async () => {
      setLoadingCpt(true)
      try {
        const result = await servicePackageService.fetchCptCodes(
          cptSearch || ''
        )
        if (isMounted) {
          setCptOptions(
            result as Array<{ id: string; code: string; description: string }>
          )
        }
      } catch (error) {
        console.error('Failed to load CPT codes', error)
        if (isMounted) {
          setCptOptions([])
        }
      } finally {
        if (isMounted) {
          setLoadingCpt(false)
        }
      }
    }, 300)

    return () => {
      isMounted = false
      window.clearTimeout(handler)
    }
  }, [cptSearch, open])

  // Fetch HCPCS codes on mount
  useEffect(() => {
    let isMounted = true
    if (!open) return

    const handler = window.setTimeout(async () => {
      setLoadingHcpcs(true)
      try {
        const result = await servicePackageService.fetchHCPCSCodes()
        if (isMounted) {
          setHcpcsOptions(result)
        }
      } catch (error) {
        console.error('Failed to load HCPCS codes', error)
        if (isMounted) {
          setHcpcsOptions([])
        }
      } finally {
        if (isMounted) {
          setLoadingHcpcs(false)
        }
      }
    }, 300)

    return () => {
      isMounted = false
      window.clearTimeout(handler)
    }
  }, [open])

  // Reset state when dialog opens or load existing note
  useEffect(() => {
    if (open) {
      if (isNewNote) {
        setNoteContent('')
        setSelectedNoteCategory(initialNoteCategory || '')
        setCptCodes([])
        setSelectedIcdCodes([])
        setSelectedCptCodes([])
        setSelectedHcpcsCodes([])
        setPatientVisible(true)
        setPdfUrl(undefined)
        setIcdInputValue('')
        setCptInputValue('')
        setHcpcsInputValue('')
        setIcdSearch('')
        setCptSearch('')
      } else if (existingNote && (existingNote._id || existingNote.id)) {
        // Initialize pdfUrl and signature from existingNote if available
        setPdfUrl(existingNote?.pdf_url || (existingNote as any)?.pdfUrl)
        setSignatureData(existingNote?.signature || null)
        setHasSavedSignature(!!existingNote?.signature)
        // Always fetch full note details to ensure we have all data
        const fetchNoteDetails = async () => {
          try {
            setLoadingNote(true)
            const noteDetailResponse = await notesApi.getNoteById(noteId)
            const noteDetail = (noteDetailResponse as any)?.data || noteDetailResponse

            setNoteDetails(noteDetail)

            // Update content
            if (noteDetail.content) {
              setNoteContent(htmlToMarkdown(noteDetail.content))
            } else if (existingNote.content) {
              setNoteContent(htmlToMarkdown(existingNote.content))
            }

            // Update category
            if (noteDetail.noteCategory) {
              const categoryId =
                typeof noteDetail.noteCategory === 'string'
                  ? noteDetail.noteCategory
                  : (noteDetail.noteCategory as NoteCategory)._id
              setSelectedNoteCategory(categoryId)
            } else if (existingNote.noteCategory) {
              const categoryId =
                typeof existingNote.noteCategory === 'string'
                  ? existingNote.noteCategory
                  : existingNote.noteCategory._id
              setSelectedNoteCategory(categoryId)
            } else if (existingNote.type && noteCategories.length > 0) {
              // Backward compatibility: map legacy 'type'
              const legacyType = String(existingNote.type).toLowerCase()
              const matchingCategory = noteCategories.find(cat =>
                cat.title.toLowerCase().includes(legacyType) ||
                cat.type?.toLowerCase().includes(legacyType)
              )
              if (matchingCategory) {
                setSelectedNoteCategory(matchingCategory._id)
              }
            }

            // Update other fields
            setCptCodes(existingNote.cptCodes || [])

            // Load CPT codes if available
            const cptCodes = (noteDetail as any).cpt || existingNote.cpt || []
            if (Array.isArray(cptCodes) && cptCodes.length > 0) {
              // Fetch CPT code details for the codes
              try {
                const cptCodePromises = cptCodes.map(async (code: string) => {
                  try {
                    const results =
                      await servicePackageService.fetchCptCodes(code)
                    const found = results.find(opt => opt.code === code)
                    return found || { id: '', code, description: code }
                  } catch {
                    return { id: '', code, description: code }
                  }
                })
                const resolvedCodes = await Promise.all(cptCodePromises)
                setSelectedCptCodes(resolvedCodes.filter(c => c.code))
              } catch (err) {
                // If fetching fails, just use the codes as-is
                setSelectedCptCodes(
                  cptCodes.map((code: string) => ({
                    id: '',
                    code,
                    description: code,
                  }))
                )
              }
            } else {
              setSelectedCptCodes([])
            }

            // Load HCPCS codes if available
            const hcpcsCodes =
              (noteDetail as any).hcpcs || existingNote.hcpcs || []
            if (Array.isArray(hcpcsCodes) && hcpcsCodes.length > 0) {
              // Fetch HCPCS code details for the codes
              try {
                const hcpcsCodePromises = hcpcsCodes.map(
                  async (code: string) => {
                    try {
                      const allHcpcs =
                        await servicePackageService.fetchHCPCSCodes()
                      const found = allHcpcs.find(opt => opt.code === code)
                      return found || { id: '', code, description: code }
                    } catch {
                      return { id: '', code, description: code }
                    }
                  }
                )
                const resolvedCodes = await Promise.all(hcpcsCodePromises)
                setSelectedHcpcsCodes(resolvedCodes.filter(c => c.code))
              } catch (err) {
                // If fetching fails, just use the codes as-is
                setSelectedHcpcsCodes(
                  hcpcsCodes.map((code: string) => ({
                    id: '',
                    code,
                    description: code,
                  }))
                )
              }
            } else {
              setSelectedHcpcsCodes([])
            }

            // Load diagnosis codes if available
            const diagnosisCodes =
              (noteDetail as any).diagnosis || existingNote.diagnosis || []
            if (Array.isArray(diagnosisCodes) && diagnosisCodes.length > 0) {
              // Fetch ICD code details for the codes
              try {
                const icdCodePromises = diagnosisCodes.map(
                  async (code: string) => {
                    try {
                      const results =
                        await servicePackageService.fetchIcdCodes(code)
                      const found = results.find(opt => opt.code === code)
                      return found || { id: '', code, description: code }
                    } catch {
                      return { id: '', code, description: code }
                    }
                  }
                )
                const resolvedCodes = await Promise.all(icdCodePromises)
                setSelectedIcdCodes(resolvedCodes.filter(c => c.code))
              } catch (err) {
                // If fetching fails, just use the codes as-is
                setSelectedIcdCodes(
                  diagnosisCodes.map((code: string) => ({
                    id: '',
                    code,
                    description: code,
                  }))
                )
              }
            } else {
              setSelectedIcdCodes([])
            }

            setPatientVisible(
              noteDetail.visibleto_patient !== undefined
                ? noteDetail.visibleto_patient
                : (existingNote.visibleto_patient ?? true)
            )

            // Update signature if available
            if ((noteDetail as any).signature) {
              setSignatureData((noteDetail as any).signature)
              setHasSavedSignature(true)
            }
          } catch (err) {
            console.error('Failed to fetch note details:', err)
            setError('Failed to load note details. Please try again.')
            // Fallback to existing note data if fetch fails
            setNoteContent(htmlToMarkdown(existingNote.content || ''))
            if (existingNote.noteCategory) {
              const categoryId =
                typeof existingNote.noteCategory === 'string'
                  ? existingNote.noteCategory
                  : existingNote.noteCategory._id
              setSelectedNoteCategory(categoryId)
            }
            setCptCodes(existingNote.cptCodes || [])

            // Load CPT codes from existing note
            const cptCodes = existingNote.cpt || []
            if (Array.isArray(cptCodes) && cptCodes.length > 0) {
              setSelectedCptCodes(
                cptCodes.map((code: string) => ({
                  id: '',
                  code,
                  description: code,
                }))
              )
            } else {
              setSelectedCptCodes([])
            }

            // Load HCPCS codes from existing note
            const hcpcsCodes = existingNote.hcpcs || []
            if (Array.isArray(hcpcsCodes) && hcpcsCodes.length > 0) {
              setSelectedHcpcsCodes(
                hcpcsCodes.map((code: string) => ({
                  id: '',
                  code,
                  description: code,
                }))
              )
            } else {
              setSelectedHcpcsCodes([])
            }

            // Load diagnosis codes from existing note
            const diagnosisCodes = existingNote.diagnosis || []
            if (Array.isArray(diagnosisCodes) && diagnosisCodes.length > 0) {
              setSelectedIcdCodes(
                diagnosisCodes.map((code: string) => ({
                  id: '',
                  code,
                  description: code,
                }))
              )
            } else {
              setSelectedIcdCodes([])
            }

            setPatientVisible(existingNote.visibleto_patient ?? true)
          } finally {
            setLoadingNote(false)
          }
        }

        fetchNoteDetails()
      } else if (existingNote) {
        // Fallback: if note exists but no ID, use provided data
        setNoteContent(htmlToMarkdown(existingNote.content || ''))
        if (existingNote.noteCategory) {
          const categoryId =
            typeof existingNote.noteCategory === 'string'
              ? existingNote.noteCategory
              : existingNote.noteCategory._id
          setSelectedNoteCategory(categoryId)
        }
        setCptCodes(existingNote.cptCodes || [])

        // Load CPT codes from existing note
        const cptCodes = existingNote.cpt || []
        if (Array.isArray(cptCodes) && cptCodes.length > 0) {
          setSelectedCptCodes(
            cptCodes.map((code: string) => ({
              id: '',
              code,
              description: code,
            }))
          )
        } else {
          setSelectedCptCodes([])
        }

        // Load HCPCS codes from existing note
        const hcpcsCodes = existingNote.hcpcs || []
        if (Array.isArray(hcpcsCodes) && hcpcsCodes.length > 0) {
          setSelectedHcpcsCodes(
            hcpcsCodes.map((code: string) => ({
              id: '',
              code,
              description: code,
            }))
          )
        } else {
          setSelectedHcpcsCodes([])
        }

        // Load diagnosis codes from existing note
        const diagnosisCodes = existingNote.diagnosis || []
        if (Array.isArray(diagnosisCodes) && diagnosisCodes.length > 0) {
          setSelectedIcdCodes(
            diagnosisCodes.map((code: string) => ({
              id: '',
              code,
              description: code,
            }))
          )
        } else {
          setSelectedIcdCodes([])
        }

        setPatientVisible(existingNote.visibleto_patient ?? true)
        setPdfUrl(existingNote?.pdf_url || (existingNote as any)?.pdfUrl)
      }
    } else {
      // Reset when dialog closes
      setNoteContent('')
      setSelectedNoteCategory('')
      setCptCodes([])
      setSelectedIcdCodes([])
      setSelectedCptCodes([])
      setSelectedHcpcsCodes([])
      setPatientVisible(true)
      setError('')
      setPdfUrl(undefined)
      setIcdInputValue('')
      setCptInputValue('')
      setHcpcsInputValue('')
      setIcdSearch('')
      setCptSearch('')
      setSelectedLanguage('en')
      setHasSavedSignature(false)
    }
  }, [open, existingNote?.id, (existingNote as any)?._id])

  // Pre-populate signature canvas when dialog opens and signature data is available
  useEffect(() => {
    if (open && signatureData && signaturePadRef.current) {
      const timer = setTimeout(() => {
        if (signaturePadRef.current && signaturePadRef.current.isEmpty()) {
          signaturePadRef.current.fromDataURL(signatureData)
        }
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [open, signatureData])

  // Scroll to top when error occurs
  useEffect(() => {
    if (error && open) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        // Find the scrollable container - Material-UI DialogContent has a scrollable div
        const scrollContainer = dialogContentRef.current?.closest(
          '.MuiDialogContent-root'
        ) as HTMLElement

        if (scrollContainer && scrollContainer.scrollTop > 0) {
          // Scroll to top smoothly
          scrollContainer.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        }
      }, 100)
    }
  }, [error, open])

  const handleRecordVoiceNote = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Audio recording is not supported in your browser')
      return
    }

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop())

        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm',
        })

        // Transcribe audio
        setIsTranscribing(true)
        try {
          const patientId = contract?.patient?.id
          if (!patientId) {
            setError('Patient ID is required for transcription')
            return
          }

          // Convert blob to File
          const audioFile = new File(
            [audioBlob],
            `recording-${Date.now()}.webm`,
            { type: 'audio/webm' }
          )

          const response = await notesApi.transcribeAudio(audioFile, patientId, selectedLanguage)
          const transcript =
            response.transcript ||
            response.text ||
            (typeof response === 'string' ? response : '')

          // Automatically split into Speaker 1 and Speaker 2
          const autoDiarize = (text: string): string => {
            if (!text || !text.trim()) return ''
            if (/^(Speaker\s*\d+|Doctor|Patient|Author\s*\d+):/mi.test(text)) {
              return text
            }
            const sentences = text.split(/(?<=[.!?])\s+/)
            const processedLines: string[] = []
            let currentSpeaker: 'Speaker 1' | 'Speaker 2' = 'Speaker 1'
            for (let i = 0; i < sentences.length; i++) {
              const sentence = sentences[i].trim()
              if (!sentence) continue
              if (i > 0) {
                const prevSentence = sentences[i - 1].trim()
                const isQuestion = prevSentence.endsWith('?')
                if (isQuestion || i % 2 === 0) {
                  currentSpeaker = currentSpeaker === 'Speaker 1' ? 'Speaker 2' : 'Speaker 1'
                }
              }
              processedLines.push(`${currentSpeaker}: ${sentence}`)
            }
            return processedLines.join('\n\n')
          }

          setNoteContent(autoDiarize(transcript))
        } catch (err: any) {
          setError(err.message || 'Failed to transcribe audio')
        } finally {
          setIsTranscribing(false)
          setIsRecording(false)
        }
      }

      mediaRecorder.onerror = (event: any) => {
        setError('Recording error occurred')
        setIsRecording(false)
        setIsTranscribing(false)
        stream.getTracks().forEach(track => track.stop())
      }

      // Start recording
      mediaRecorder.start()
      setIsRecording(true)
      setError('')
    } catch (err: any) {
      setError(
        err.message ||
        'Failed to access microphone. Please check your permissions.'
      )
      setIsRecording(false)
    }
  }

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear()
      setSignatureData(null)
    }
  }

  const handleSignatureEnd = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const dataURL = signaturePadRef.current.toDataURL('image/png')
      setSignatureData(dataURL)
    } else {
      setSignatureData(null)
    }
  }

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
    }
    setIsRecording(false)
  }

  // Typing effect for AI-generated content
  const typeText = async (text: string) => {
    setNoteContent('')
    const words = text.split(' ')

    for (let i = 0; i < words.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 30)) // Adjust speed here
      setNoteContent(prev => {
        const newText = prev + (prev ? ' ' : '') + words[i]
        return newText
      })

      // Auto-scroll to bottom of textarea
      if (noteInputRef.current) {
        noteInputRef.current.scrollTop = noteInputRef.current.scrollHeight
      }
    }
  }

  const handleRefineWithAI = async () => {
    if (!noteContent.trim()) {
      setError('Please enter or record some note content first')
      return
    }

    if (!selectedNoteCategory) {
      setError('Please select a note category first')
      return
    }

    const contractId = contract?.id || session.contractId || session.contract_id
    const sessionId = session._id || session.id

    if (!contractId || !sessionId) {
      setError('Contract ID and Session ID are required')
      return
    }

    setIsRefining(true)
    setError('')

    try {
      const selectedCategory = noteCategories.find(
        cat => cat._id === selectedNoteCategory
      )
      if (!selectedCategory) {
        setError('Invalid note category selected')
        return
      }

      // Extract diagnosis codes (only codes, not IDs)
      const diagnosisCodes = selectedIcdCodes
        .map(icd => icd.code)
        .filter(Boolean)
      const cptCodes = selectedCptCodes.map(cpt => cpt.code).filter(Boolean)
      const hcpcsCodes = selectedHcpcsCodes
        .map(hcpcs => hcpcs.code)
        .filter(Boolean)

      const response = await notesApi.generateNote({
        noteType: selectedCategory.type,
        therapistSummary: noteContent,
        contractId: contractId,
        sessionID: sessionId,
        diagnosis: diagnosisCodes.length > 0 ? diagnosisCodes : undefined,
        cpt: cptCodes.length > 0 ? cptCodes : undefined,
        hcpcs: hcpcsCodes.length > 0 ? hcpcsCodes : undefined,
      })

      // Extract the generated note content
      const generatedContent =
        response.generatedNote ||
        response.content ||
        response.note ||
        response.text ||
        (typeof response === 'string' ? response : '')

      if (!generatedContent) {
        setError('No content received from AI generation')
        return
      }

      // 1. Convert the first line to a centered title if it's not already formatted
      // 2. Identify and bold subheadings (lines ending with colons)
      let processedContent = generatedContent.trim()

      const lines = processedContent.split('\n')
      const formattedLines = lines.map((line, index) => {
        const trimmedLine = line.trim()
        if (!trimmedLine) return line

        // Handle the very first line as the Main Title (Centered, Bold, 20px)
        if (index === 0) {
          const cleanTitle = trimmedLine.replace(/^[*-\s]+/, '').replace(/\*\*+/g, '')
          return `**${cleanTitle}**`
        }

        // Check if this line is a subheading (ends with colon)
        const isSubheading = trimmedLine.endsWith(':') && trimmedLine.length < 60

        if (isSubheading) {
          // Force bullet and bolding for subheadings
          const cleanSub = trimmedLine.replace(/^[*-\s]+/, '').replace(/\*\*+/g, '')
          return `- **${cleanSub}**`
        } else {
          // For all other lines: STRIP bullets and STRIP bolding
          const cleanText = trimmedLine.replace(/^[*-\s]+/, '').replace(/\*\*+/g, '')
          return cleanText
        }
      })

      processedContent = formattedLines.join('\n')

      // Type out the generated note with animation
      await typeText(processedContent)
    } catch (err: any) {
      // Check if it's a timeout error
      if (
        err.code === 'ECONNABORTED' ||
        err.message?.includes('timeout') ||
        err.message?.includes('Timeout')
      ) {
        setError(
          'AI note generation is taking longer than expected. Please try again or use a shorter summary.'
        )
      } else {
        setError(
          err.response?.data?.message ||
          err.message ||
          'Failed to generate note with AI. Please try again.'
        )
      }
    } finally {
      setIsRefining(false)
    }
  }

  const handleSaveDraft = async () => {
    if (!noteContent.trim()) {
      setError('Note content is required')
      return
    }

    if (!selectedNoteCategory) {
      setError('Please select a note category')
      return
    }

    const contractId = contract?.id || session.contractId || session.contract_id
    const sessionId = session._id || session.id

    if (!contractId || !sessionId) {
      setError('Contract ID and Session ID are required')
      return
    }

    setSavingDraft(true)
    setError('')
    try {
      // Extract diagnosis codes (only codes, not IDs)
      const diagnosisCodes = selectedIcdCodes
        .map(icd => icd.code)
        .filter(Boolean)
      const cptCodes = selectedCptCodes.map(cpt => cpt.code).filter(Boolean)
      const hcpcsCodes = selectedHcpcsCodes
        .map(hcpcs => hcpcs.code)
        .filter(Boolean)

      // Get latest signature from pad if it's currently showing
      let currentSignature = signatureData
      if (!hasSavedSignature && signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        currentSignature = signaturePadRef.current.toDataURL('image/png')
      }

      let response
      if (noteId && !isNewNote) {
        // Update existing note
        response = await notesApi.updateNote(noteId, {
          contractId: contractId,
          sessionId: sessionId,
          content: renderedNoteHtml,
          noteCategory: selectedNoteCategory,
          visibleto_patient: patientVisible,
          status: 'draft',
          diagnosis: diagnosisCodes.length > 0 ? diagnosisCodes : undefined,
          cpt: cptCodes.length > 0 ? cptCodes : undefined,
          hcpcs: hcpcsCodes.length > 0 ? hcpcsCodes : undefined,
          signature: currentSignature || undefined,
        })
      } else {
        // Create new note
        response = await notesApi.saveNote({
          contractId: contractId,
          sessionId: sessionId,
          content: renderedNoteHtml,
          noteCategory: selectedNoteCategory,
          visibleto_patient: patientVisible,
          status: 'draft',
          diagnosis: diagnosisCodes.length > 0 ? diagnosisCodes : undefined,
          cpt: cptCodes.length > 0 ? cptCodes : undefined,
          hcpcs: hcpcsCodes.length > 0 ? hcpcsCodes : undefined,
          signature: currentSignature || undefined,
        })
      }

      const realResponse = (response as any)?.data || response
      // Convert API response to Note type matching API structure
      const savedNote: Note = {
        _id: realResponse._id,
        contractId: realResponse.contractId || contractId,
        sessionId: realResponse.sessionId,
        content: realResponse.content,
        status: (realResponse.status?.toLowerCase() === 'finalized'
          ? 'finalized'
          : 'draft') as NoteStatus,
        visibleto_patient: realResponse.visibleto_patient,
        noteCategory: realResponse.noteCategory || {
          _id: selectedNoteCategory,
          title:
            noteCategories.find(cat => cat._id === selectedNoteCategory)
              ?.title || 'Clinical Note',
          type: 'soap_note',
          is_active: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        createdAt: realResponse.createdAt,
        updatedAt: realResponse.updatedAt,
        isActive: realResponse.isActive ?? true,
        isApprovedAgency: realResponse.isApprovedAgency,
        signature: currentSignature || realResponse.signature || undefined,
      }

      onSuccess(savedNote)
      onClose()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || ''
      const statusCode = err.response?.status || err.statusCode

      // Handle specific "Notes already exist" error
      if (
        statusCode === 400 &&
        (errorMessage.includes('Notes already exist') ||
          errorMessage.includes('notes already exist'))
      ) {
        const categoryTitle =
          noteCategories.find(cat => cat._id === selectedNoteCategory)?.title ||
          'this note type'
        setError(
          `A ${categoryTitle} already exists for this session. Please edit the existing note or select a different note category.`
        )
      } else {
        setError(errorMessage || 'Failed to save note. Please try again.')
      }
    } finally {
      setSavingDraft(false)
    }
  }

  const handleFinalize = async () => {
    if (!noteContent.trim()) {
      setError(
        'Note content is required. Please refine your notes with AI first.'
      )
      return
    }

    if (!selectedNoteCategory) {
      setError('Please select a note category')
      return
    }

    const contractId = contract?.id || session.contractId || session.contract_id
    const sessionId = session._id || session.id

    if (!contractId || !sessionId) {
      setError('Contract ID and Session ID are required')
      return
    }

    setFinalizing(true)
    setError('')
    try {
      // Extract diagnosis codes (only codes, not IDs)
      const diagnosisCodes = selectedIcdCodes
        .map(icd => icd.code)
        .filter(Boolean)
      const cptCodes = selectedCptCodes.map(cpt => cpt.code).filter(Boolean)
      const hcpcsCodes = selectedHcpcsCodes
        .map(hcpcs => hcpcs.code)
        .filter(Boolean)

      // Get latest signature from pad if it's currently showing
      let currentSignature = signatureData
      if (!hasSavedSignature && signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        currentSignature = signaturePadRef.current.toDataURL('image/png')
      }

      let response
      if (noteId && !isNewNote) {
        // Update existing note
        response = await notesApi.updateNote(noteId, {
          contractId: contractId,
          sessionId: sessionId,
          content: renderedNoteHtml,
          noteCategory: selectedNoteCategory,
          visibleto_patient: patientVisible,
          status: 'finalized',
          diagnosis: diagnosisCodes.length > 0 ? diagnosisCodes : undefined,
          cpt: cptCodes.length > 0 ? cptCodes : undefined,
          hcpcs: hcpcsCodes.length > 0 ? hcpcsCodes : undefined,
          signature: currentSignature || undefined,
        })
      } else {
        // Create new note
        response = await notesApi.saveNote({
          contractId: contractId,
          sessionId: sessionId,
          content: renderedNoteHtml,
          noteCategory: selectedNoteCategory,
          visibleto_patient: patientVisible,
          status: 'finalized',
          diagnosis: diagnosisCodes.length > 0 ? diagnosisCodes : undefined,
          cpt: cptCodes.length > 0 ? cptCodes : undefined,
          hcpcs: hcpcsCodes.length > 0 ? hcpcsCodes : undefined,
          signature: currentSignature || undefined,
        })
      }

      const realResponse = (response as any)?.data || response
      // Convert API response to Note type matching API structure
      const finalizedNote: Note = {
        _id: realResponse._id,
        contractId: realResponse.contractId || contractId,
        sessionId: realResponse.sessionId,
        content: realResponse.content,
        status: 'finalized' as NoteStatus,
        visibleto_patient: realResponse.visibleto_patient,
        noteCategory: realResponse.noteCategory || {
          _id: selectedNoteCategory,
          title:
            noteCategories.find(cat => cat._id === selectedNoteCategory)
              ?.title || 'Clinical Note',
          type: 'soap_note',
          is_active: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        createdAt: realResponse.createdAt,
        updatedAt: realResponse.updatedAt,
        isActive: realResponse.isActive ?? true,
        isApprovedAgency: realResponse.isApprovedAgency,
        signature: currentSignature || realResponse.signature || undefined,
      }

      onSuccess(finalizedNote)
      onClose()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || ''
      const statusCode = err.response?.status || err.statusCode

      // Handle specific "Notes already exist" error
      if (
        statusCode === 400 &&
        (errorMessage.includes('Notes already exist') ||
          errorMessage.includes('notes already exist'))
      ) {
        const categoryTitle =
          noteCategories.find(cat => cat._id === selectedNoteCategory)?.title ||
          'this note type'
        setError(
          `A ${categoryTitle} already exists for this session. Please edit the existing note or select a different note category.`
        )
      } else {
        setError(errorMessage || 'Failed to finalize note. Please try again.')
      }
    } finally {
      setFinalizing(false)
    }
  }

  const handleDeleteClick = () => {
    if (!noteId) {
      setError('Note ID is required for deletion')
      return
    }
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async () => {
    if (!noteId) {
      setError('Note ID is required for deletion')
      setShowDeleteDialog(false)
      return
    }

    setIsDeleting(true)
    setError('')
    setShowDeleteDialog(false)
    try {
      await notesApi.deleteNote(noteId)
      // Call onSuccess with deleted note structure to signal deletion
      // The parent component should handle refreshing the session
      const contractId =
        contract?.id || session.contractId || session.contract_id || ''
      onSuccess({
        _id: noteId,
        contractId: contractId,
        sessionId: session._id || session.id,
        content: '',
        status: 'draft' as NoteStatus,
        visibleto_patient: false,
        createdAt: '',
        updatedAt: '',
        isActive: false,
        isDeleted: true,
      })
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to delete note')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false)
  }

  const handleDownloadPDF = () => {
    const url = pdfUrl || existingNote?.pdf_url || (existingNote as any)?.pdfUrl

    if (!url) {
      setError('PDF download URL is not available for this note')
      return
    }

    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a')
      link.href = url
      link.download = `note-${noteId || 'download'}.pdf`
      link.target = '_blank'
      link.rel = 'noopener noreferrer'

      // Append to body, click, and remove
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err: any) {
      setError(err.message || 'Failed to download PDF. Please try again.')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            {isFinalized ? (
              <LockIcon color="action" />
            ) : (
              <DescriptionIcon color="primary" />
            )}
            <Typography variant="h6" fontWeight={600}>
              {isReadOnly
                ? 'View Note'
                : existingNote
                  ? 'Edit Note'
                  : 'Create Clinical Note'}
            </Typography>
            {isFinalized && (
              <Chip label="Finalized" color="success" size="small" />
            )}
            {isSessionCompleted && !isFinalized && (
              <Chip label="Session Completed" color="info" size="small" />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ py: 3 }}>
        <Box ref={dialogContentRef}>
          {error && (
            <Fade in>
              <Alert
                severity="error"
                sx={{ mb: 3 }}
                onClose={() => setError('')}
              >
                {error}
              </Alert>
            </Fade>
          )}

          {loadingNote && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading note content...
              </Typography>
            </Box>
          )}

          {!loadingNote && isReadOnly && (
            <Alert
              severity={forceReadOnly ? 'error' : 'info'}
              sx={{ mb: 3 }}
              icon={<LockIcon />}
            >
              {forceReadOnly
                ? 'Contract is cancelled. Notes can only be viewed in read-only mode.'
                : 'This note is finalized and cannot be edited.'}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Top Row: Note Category and ICD-10 together to reduce height */}
            <Box >
              {/* Note Category Selection */}
              <Box>
                {/* <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 1.5 }}
                >
                  Note Category
                </Typography> */}
                {!isReadOnly ? (
                  <FormControl fullWidth>
                    <InputLabel>Select Category</InputLabel>
                    {loadingCategories ? (
                      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <Select
                        value={selectedNoteCategory}
                        label="Select Category"
                        onChange={e => setSelectedNoteCategory(e.target.value)}
                        disabled={loadingCategories}
                      >
                        {noteCategories.map(category => (
                          <MenuItem key={category._id} value={category._id}>
                            {category.title}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                ) : (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" color="text.primary">
                      {selectedCategoryTitle || 'No Category Selected'}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* ICD Code Selection */}
              <Box>
                {!isReadOnly && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ mb: 1.5 }}
                    >
                      ICD-10 Diagnosis Codes (Optional)
                    </Typography>
                    <Autocomplete
                      options={icdOptions.filter(option => {
                        // Filter out already selected ICD codes
                        return !selectedIcdCodes.some(selected => {
                          // Match by code
                          return selected.code === option.code
                        })
                      })}
                      getOptionLabel={option =>
                        option.description
                          ? `${option.code} - ${option.description}`
                          : option.code
                      }
                      value={null}
                      inputValue={icdInputValue}
                      onChange={(_, value) => {
                        if (value) {
                          // Check if already selected
                          const isAlreadySelected = selectedIcdCodes.some(
                            selected => selected.code === value.code
                          )
                          if (!isAlreadySelected) {
                            setSelectedIcdCodes([...selectedIcdCodes, value])
                            setIcdInputValue('')
                            setIcdSearch('')
                          }
                        }
                      }}
                      onInputChange={(_, value) => {
                        setIcdInputValue(value)
                        setIcdSearch(value)
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.code}
                            </Typography>
                            {option.description && (
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Search ICD-10 Codes"
                          placeholder="Type to search ICD-10 codes..."
                        />
                      )}
                      loading={loadingIcd}
                      isOptionEqualToValue={(option, value) =>
                        option.code === value.code
                      }
                      disabled={isReadOnly}
                    />
                    {selectedIcdCodes.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Selected ICD-10 Codes:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedIcdCodes.map((icdCode, index) => (
                            <Chip
                              key={`${icdCode.code}-${index}`}
                              label={
                                icdCode.description
                                  ? `${icdCode.code} - ${icdCode.description}`
                                  : icdCode.code
                              }
                              onDelete={() => {
                                setSelectedIcdCodes(
                                  selectedIcdCodes.filter((_, i) => i !== index)
                                )
                              }}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Read-only ICD Codes Display */}
                {isReadOnly && selectedIcdCodes.length > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      ICD-10 Diagnosis Codes:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedIcdCodes.map((icdCode, index) => (
                        <Chip
                          key={`${icdCode.code}-${index}`}
                          label={
                            icdCode.description
                              ? `${icdCode.code} - ${icdCode.description}`
                              : icdCode.code
                          }
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Codes Row: CPT and HCPCS together to reduce height */}
            <Box >
              {/* CPT Code Selection */}
              <Box>
                {!isReadOnly && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ mb: 1.5 }}
                    >
                      CPT Codes (Optional)
                    </Typography>
                    <Autocomplete
                      options={cptOptions.filter(option => {
                        // Filter out already selected CPT codes
                        return !selectedCptCodes.some(selected => {
                          return selected.code === option.code
                        })
                      })}
                      getOptionLabel={option =>
                        option.description
                          ? `${option.code} - ${option.description}`
                          : option.code
                      }
                      value={null}
                      inputValue={cptInputValue}
                      onChange={(_, value) => {
                        if (value) {
                          // Check if already selected
                          const isAlreadySelected = selectedCptCodes.some(
                            selected => selected.code === value.code
                          )
                          if (!isAlreadySelected) {
                            setSelectedCptCodes([...selectedCptCodes, value])
                            setCptInputValue('')
                            setCptSearch('')
                          }
                        }
                      }}
                      onInputChange={(_, value) => {
                        setCptInputValue(value)
                        setCptSearch(value)
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.code}
                            </Typography>
                            {option.description && (
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Search CPT Codes"
                          placeholder="Type to search CPT codes..."
                        />
                      )}
                      loading={loadingCpt}
                      isOptionEqualToValue={(option, value) =>
                        option.code === value.code
                      }
                      disabled={isReadOnly}
                    />
                    {selectedCptCodes.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Selected CPT Codes:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedCptCodes.map((cptCode, index) => (
                            <Chip
                              key={`${cptCode.code}-${index}`}
                              label={
                                cptCode.description
                                  ? `${cptCode.code} - ${cptCode.description}`
                                  : cptCode.code
                              }
                              onDelete={() => {
                                setSelectedCptCodes(
                                  selectedCptCodes.filter((_, i) => i !== index)
                                )
                              }}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Read-only CPT Codes Display */}
                {isReadOnly && selectedCptCodes.length > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      CPT Codes:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedCptCodes.map((cptCode, index) => (
                        <Chip
                          key={`${cptCode.code}-${index}`}
                          label={
                            cptCode.description
                              ? `${cptCode.code} - ${cptCode.description}`
                              : cptCode.code
                          }
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* HCPCS Code Selection */}
              <Box>
                {!isReadOnly && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ mb: 1.5, mt: 2 }}
                    >
                      HCPCS Level II Codes (Optional)
                    </Typography>
                    <Autocomplete
                      options={hcpcsOptions.filter(option => {
                        // Filter out already selected HCPCS codes
                        return !selectedHcpcsCodes.some(selected => {
                          return selected.code === option.code
                        })
                      })}
                      getOptionLabel={option =>
                        option.description
                          ? `${option.code} - ${option.description}`
                          : option.code
                      }
                      value={null}
                      inputValue={hcpcsInputValue}
                      onChange={(_, value) => {
                        if (value) {
                          // Check if already selected
                          const isAlreadySelected = selectedHcpcsCodes.some(
                            selected => selected.code === value.code
                          )
                          if (!isAlreadySelected) {
                            setSelectedHcpcsCodes([...selectedHcpcsCodes, value])
                            setHcpcsInputValue('')
                          }
                        }
                      }}
                      onInputChange={(_, value) => {
                        setHcpcsInputValue(value)
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {option.code}
                            </Typography>
                            {option.description && (
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Search HCPCS Level II Codes"
                          placeholder="Type to search HCPCS codes..."
                        />
                      )}
                      loading={loadingHcpcs}
                      isOptionEqualToValue={(option, value) =>
                        option.code === value.code
                      }
                      disabled={isReadOnly}
                    />
                    {selectedHcpcsCodes.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Selected HCPCS Level II Codes:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedHcpcsCodes.map((hcpcsCode, index) => (
                            <Chip
                              key={`${hcpcsCode.code}-${index}`}
                              label={
                                hcpcsCode.description
                                  ? `${hcpcsCode.code} - ${hcpcsCode.description}`
                                  : hcpcsCode.code
                              }
                              onDelete={() => {
                                setSelectedHcpcsCodes(
                                  selectedHcpcsCodes.filter((_, i) => i !== index)
                                )
                              }}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Read-only HCPCS Codes Display */}
                {isReadOnly && selectedHcpcsCodes.length > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      HCPCS Level II Codes:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedHcpcsCodes.map((hcpcsCode, index) => (
                        <Chip
                          key={`${hcpcsCode.code}-${index}`}
                          label={
                            hcpcsCode.description
                              ? `${hcpcsCode.code} - ${hcpcsCode.description}`
                              : hcpcsCode.code
                          }
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Prescription-style Header / Demographics */}
            {!loadingNote && contract && (
              <NoteDemographicsHeader
                contract={contract}
                selectedCategoryTitle={selectedCategoryTitle}
                selectedIcdCodes={selectedIcdCodes}
                selectedCptCodes={selectedCptCodes}
                selectedHcpcsCodes={selectedHcpcsCodes}
              />
            )}

            {/* Note Content Display/Input */}
            {!loadingNote && isReadOnly ? (
              /* Read-only view for finalized/completed notes */
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  minHeight: '300px',
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                  Note Content
                </Typography>
                <Box
                  sx={{
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    color: 'text.primary',
                    minHeight: '250px',
                    '& > h1:first-of-type, & > h2:first-of-type': {
                      textAlign: 'center',
                      fontSize: '20px',
                      fontWeight: '700',
                      marginBottom: '2rem',
                      display: 'block',
                      '& *': { fontSize: '20px', fontWeight: '700' },
                    },
                    '& strong, & b': {
                      fontSize: '16px !important',
                      fontWeight: '700 !important',
                      display: 'inline-flex !important',
                      alignItems: 'center !important',
                      marginTop: '0.75rem !important',
                      marginBottom: '0 !important',
                      marginLeft: '-1.5rem !important',
                      '&::before': {
                        content: '""',
                        display: 'inline-block',
                        width: '7px',
                        height: '7px',
                        borderRadius: '50%',
                        backgroundColor: 'text.primary',
                        marginRight: '12px',
                        flexShrink: 0,
                      },
                    },
                    // Disable custom bullet before numbers in ordered lists
                    '& ol li strong, & ol li b': {
                      '&::before': {
                        display: 'none !important',
                      },
                    },
                    // Main Title (first top-level element) - No bullet, larger text
                    '& > h1:first-of-type strong, & > h2:first-of-type strong': {
                      fontSize: '20px !important',
                      marginTop: '0 !important',
                      marginBottom: '0 !important',
                      display: 'inline-block !important',
                      width: '100% !important',
                      textAlign: 'center !important',
                      marginLeft: '0 !important',
                      '&::before': {
                        display: 'none !important',
                      },
                    },
                    '& p, & span, & div': {
                      fontSize: '12px !important',
                      lineHeight: '1.6 !important',
                      margin: '0 0 0.5rem !important',
                      paddingLeft: '1.5rem !important',
                    },
                    '& ul, & ol': {
                      paddingLeft: '1.5rem !important',
                      margin: '1rem 0 !important',
                      '& li::marker': {
                        color: 'text.primary',
                        fontSize: '1.1em !important',
                      },
                    },
                    '& li': {
                      marginBottom: '0.1rem !important',
                      paddingLeft: '0.25rem !important',
                    },
                    '& hr': { display: 'none' },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: renderedNoteHtml || 'No content available.',
                  }}
                />
              </Paper>
            ) : !loadingNote ? (
              /* Editable input section for draft notes */
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Notes Input — Choose One Option
                  </Typography>
                  <Box display="flex" gap={1}>
                    {[
                      { label: 'English', value: 'en' },
                      { label: 'Tagalog', value: 'tl' },
                      { label: 'Spanish', value: 'es' },
                      { label: 'Mandarin', value: 'zh' }
                    ].map((lang) => (
                      <Button
                        key={lang.value}
                        size="small"
                        variant={selectedLanguage === lang.value ? "contained" : "outlined"}
                        onClick={() => setSelectedLanguage(lang.value)}
                        disabled={isRecording || isTranscribing}
                        sx={{
                          borderRadius: '20px',
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          py: 0.25,
                          px: 1.5,
                          minWidth: 'auto',
                          ...(selectedLanguage === lang.value ? {
                            background: 'linear-gradient(135deg, #4a00e0, #8e2de2)',
                            color: '#fff',
                            border: 'none',
                            boxShadow: '0 2px 8px rgba(74, 0, 224, 0.3)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #3b00b3, #7223b3)',
                            }
                          } : {
                            borderColor: 'divider',
                            color: 'text.secondary',
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: 'rgba(74, 0, 224, 0.04)',
                            }
                          })
                        }}
                      >
                        {lang.label}
                      </Button>
                    ))}
                  </Box>
                </Box>

                <Stack spacing={3}>
                  {/* Option 1: Record Voice Note */}
                  <Box>
                    {isRecording ? (
                      <Button
                        startIcon={<RadioButtonCheckedIcon />}
                        variant="contained"
                        color="error"
                        onClick={handleStopRecording}
                        fullWidth
                        sx={{
                          py: 1.5,
                          animation: `${pulseAnimation} 1.5s infinite`,
                        }}
                      >
                        Stop Recording
                      </Button>
                    ) : (
                      <Button
                        startIcon={
                          isTranscribing ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <MicIcon />
                          )
                        }
                        variant="outlined"
                        color="primary"
                        onClick={handleRecordVoiceNote}
                        disabled={isTranscribing}
                        fullWidth
                        sx={{
                          py: 1.5,
                        }}
                      >
                        {isTranscribing
                          ? 'Transcribing...'
                          : 'Record Voice Note'}
                      </Button>
                    )}
                  </Box>

                  {/* Minimal OR Divider */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      my: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    >
                      OR
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  </Box>

                  {/* Option 2: Type Manually */}
                  <Box>
                    {/* <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ textAlign: 'center' }}
                    >
                      {selectedCategoryTitle}
                    </Typography> */}

                    <Box
                      ref={noteInputRef}
                      contentEditable={
                        !isRecording && !isRefining && !isTranscribing
                      }
                      suppressContentEditableWarning
                      onFocus={() => {
                        isUserEditingRef.current = true
                      }}
                      onBlur={event => {
                        isUserEditingRef.current = false
                        const target = event.currentTarget as HTMLDivElement
                        // Capture HTML and convert to markdown for storage
                        const htmlContent = target.innerHTML || ''
                        const markdownContent = htmlToMarkdown(htmlContent)
                        setNoteContent(markdownContent)
                      }}
                      onInput={event => {
                        const target = event.currentTarget as HTMLDivElement
                        // Capture HTML and convert to markdown for storage
                        const htmlContent = target.innerHTML || ''
                        const markdownContent = htmlToMarkdown(htmlContent)
                        setNoteContent(markdownContent)
                      }}
                      data-placeholder="Start typing your clinical findings here..."
                      sx={{
                        minHeight: 450,
                        maxHeight: 600,
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        borderRadius: '0 0 8px 8px',
                        border: '1px solid',
                        borderTop: 'none',
                        borderColor: isRefining ? 'secondary.main' : 'divider',
                        padding: 4,
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        fontFamily: "'Inter', 'Roboto', sans-serif",
                        color: '#2c3e50',
                        lineHeight: 1.8,
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
                        '&:empty:before': {
                          content: 'attr(data-placeholder)',
                          color: 'text.disabled',
                          fontStyle: 'italic',
                        },
                        // Document Section Styling
                        '& > h1:first-of-type, & > h2:first-of-type': {
                          textAlign: 'center',
                          fontSize: '1.25rem',
                          fontWeight: '800',
                          color: 'primary.dark',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '2.5rem',
                          marginTop: '0.5rem',
                          display: 'block',
                          borderBottom: '2px solid',
                          borderColor: 'primary.light',
                          pb: 1,
                          '& *': { fontSize: 'inherit', fontWeight: 'inherit' },
                        },
                        '& strong, & b': {
                          fontSize: '0.95rem',
                          fontWeight: '700',
                          color: 'primary.main',
                          display: 'inline-flex',
                          alignItems: 'center',
                          marginTop: '1.25rem',
                          marginBottom: '0.25rem',
                          marginLeft: '-1.5rem',
                          '&::before': {
                            content: '""',
                            display: 'inline-block',
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: 'primary.main',
                            marginRight: '12px',
                            flexShrink: 0,
                          },
                        },
                        // Only disable custom bullets before numbers in ordered lists
                        '& ol li strong, & ol li b': {
                          '&::before': {
                            display: 'none !important',
                          },
                        },
                        // Title selector - only target the absolute first paragraph of the note
                        '& > h1:first-of-type strong, & > h2:first-of-type strong': {
                          fontSize: '1.25rem',
                          marginTop: 0,
                          marginBottom: 0,
                          display: 'inline-block',
                          width: '100%',
                          textAlign: 'center',
                          marginLeft: 0,
                          border: 'none',
                          '&::before': {
                            display: 'none !important',
                          },
                        },
                        '& p, & li, & span, & div': {
                          fontSize: '0.95rem',
                          lineHeight: 1.8,
                          margin: '0 0 0.85rem',
                          paddingLeft: '1.5rem',
                        },

                        '& ul, & ol': {
                          paddingLeft: '1.5rem',
                          margin: '0 0 1.5rem',
                          '& li::marker': {
                            color: 'primary.main',
                            fontWeight: '700',
                          },
                        },
                        '& li': {
                          marginBottom: '0.75rem',
                          paddingLeft: '0.5rem',
                        },
                        '& hr': {
                          border: 'none',
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          my: 3,
                        },
                      }}
                    />
                  </Box>

                  {/* Divider before AI section */}
                  <Divider sx={{ my: 2 }} />

                  {/* AI Refinement Section */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1.5, fontWeight: 600 }}
                    >
                      Refine Your Note (Optional)
                    </Typography>
                    <Button
                      startIcon={
                        isRefining ? (
                          <CircularProgress size={20} />
                        ) : (
                          <AutoAwesomeIcon />
                        )
                      }
                      variant="contained"
                      onClick={handleRefineWithAI}
                      disabled={isRefining || !noteContent.trim() || isReadOnly}
                      fullWidth
                      sx={{
                        py: 1.6,
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: '#fff',

                        background: 'linear-gradient(135deg, #8e2de2, #4a00e0)',

                        borderRadius: '14px',

                        backdropFilter: 'blur(10px)',

                        boxShadow: `
    0 4px 20px rgba(142, 45, 226, 0.4),
    inset 0 0 10px rgba(255,255,255,0.1)
  `,

                        transition: 'all 0.3s ease',

                        '& .MuiButton-startIcon': {
                          color: '#fff !important',
                        },

                        // 🔥 VERY IMPORTANT (disabled override)
                        '&.Mui-disabled': {
                          color: '#fff !important',
                          opacity: 0.7,
                        },
                        '&:hover': {
                          transform: 'translateY(-2px) scale(1.02)',
                          boxShadow: `
      0 8px 30px rgba(142, 45, 226, 0.6),
      inset 0 0 12px rgba(255,255,255,0.15)
    `,
                        },
                      }}
                    >
                      {isRefining ? 'Refining with AI...' : 'Refine with AI'}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            ) : null}

            {/* Old Input Section - Remove this */}
            {false && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Notes Input — Choose One Option
                  </Typography>
                </Box>

                <Stack spacing={3}>
                  {/* Option 1: Record Voice Note */}
                  <Box>
                    {isRecording ? (
                      <Button
                        startIcon={<RadioButtonCheckedIcon />}
                        variant="contained"
                        color="error"
                        onClick={handleStopRecording}
                        fullWidth
                        sx={{
                          py: 1.5,
                          animation: `${pulseAnimation} 1.5s infinite`,
                        }}
                      >
                        Stop Recording
                      </Button>
                    ) : (
                      <Button
                        startIcon={
                          isTranscribing ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <MicIcon />
                          )
                        }
                        variant="outlined"
                        color="primary"
                        onClick={handleRecordVoiceNote}
                        disabled={isTranscribing || isFinalized}
                        fullWidth
                        sx={{
                          py: 1.5,
                        }}
                      >
                        {isTranscribing
                          ? 'Transcribing...'
                          : 'Record Voice Note'}
                      </Button>
                    )}
                  </Box>

                  {/* Minimal OR Divider */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      my: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    >
                      OR
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  </Box>

                  {/* Option 2: Type Manually */}
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={14}
                      value={noteContent}
                      onChange={e => setNoteContent(e.target.value)}
                      placeholder="Type your clinical notes here..."
                      disabled={
                        isFinalized ||
                        isRecording ||
                        isRefining ||
                        isTranscribing
                      }
                      ref={noteInputRef}
                      sx={{
                        '& .MuiInputBase-root': {
                          backgroundColor: 'white',
                          transition: 'all 0.3s ease',
                          borderColor: isRefining
                            ? 'secondary.main'
                            : 'divider',
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.95rem',
                          lineHeight: 1.7,
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                        },
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: isRefining
                              ? 'secondary.main'
                              : 'primary.main',
                            borderWidth: isRefining ? '2px' : '1px',
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* Divider before AI section */}
                  <Divider sx={{ my: 2 }} />

                  {/* AI Refinement Section */}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1.5, fontWeight: 600 }}
                    >
                      Refine Your Note (Optional)
                    </Typography>
                    <Button
                      startIcon={
                        isRefining ? (
                          <CircularProgress size={20} />
                        ) : (
                          <AutoAwesomeIcon />
                        )
                      }
                      variant="contained"
                      color="secondary"
                      onClick={handleRefineWithAI}
                      disabled={isRefining || !noteContent.trim() || isReadOnly}
                      fullWidth
                      sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        background:
                          'linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)',
                        boxShadow: '0 3px 10px 2px rgba(156, 39, 176, .3)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #7b1fa2 30%, #9c27b0 90%)',
                        },
                      }}
                    >
                      {isRefining ? 'Refining with AI...' : 'Refine with AI ✨'}
                    </Button>

                    <Alert
                      severity="info"
                      icon={<AutoAwesomeIcon />}
                      sx={{ mt: 2 }}
                    >
                      <Typography variant="caption">
                        AI will generate a professional clinical note based on
                        your voice recording or typed notes
                      </Typography>
                    </Alert>
                  </Box>
                </Stack>
              </Paper>
            )}

            {/* Therapist Signature Section */}
            {!isReadOnly && (
              <Box sx={{ mt: 2, mb: 1 }}>
                {hasSavedSignature && signatureData ? (
                  <>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1, fontWeight: 500 }}
                    >
                      Therapist Signature:
                    </Typography>
                    <Box
                      sx={{
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'white',
                        width: 'fit-content',
                      }}
                    >
                      <img
                        src={signatureData}
                        alt="Therapist Signature"
                        style={{ maxHeight: 80, display: 'block' }}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setHasSavedSignature(false)
                        setSignatureData(null)
                      }}
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      Edit Signature
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1, fontWeight: 500 }}
                    >
                      Therapist Signature (draw below){' '}
                      <Typography component="span" sx={{ color: 'error.main' }}>
                        *
                      </Typography>
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        border: '2px solid',
                        borderColor: signatureData ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        p: 1,
                        bgcolor: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        overflow: 'auto',
                        '& canvas': {
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          cursor: 'crosshair',
                          maxWidth: '100%',
                          height: 'auto',
                        },
                      }}
                    >
                      <SignatureCanvas
                        ref={signaturePadRef}
                        canvasProps={{
                          width: 800,
                          height: 150,
                          className: 'signature-canvas',
                          style: { maxWidth: '100%', height: 'auto' },
                        }}
                        onEnd={handleSignatureEnd}
                      />
                    </Paper>

                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleClearSignature}
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      Clear Signature
                    </Button>
                  </>
                )}
              </Box>
            )}

            {/* Read-only Signature Display */}
            {isReadOnly && signatureData && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  Therapist Signature:
                </Typography>
                <Box
                  sx={{
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'white',
                    width: 'fit-content',
                  }}
                >
                  <img
                    src={signatureData}
                    alt="Therapist Signature"
                    style={{ maxHeight: 80, display: 'block' }}
                  />
                </Box>
              </Box>
            )}

            {/* Patient Visibility Toggle */}
            {!hasAgencyId && !isReadOnly && (
              <FormControlLabel
                control={
                  <Switch
                    checked={patientVisible}
                    onChange={e => setPatientVisible(e.target.checked)}
                    disabled={isReadOnly}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Make this note visible to patient in their portal
                  </Typography>
                }
              />
            )}
            {(() => {
              const statusVal = (noteDetails?.status || existingNote?.status || '').toString().toLowerCase()
              const isApproved = noteDetails?.isApprovedAgency || existingNote?.isApprovedAgency || statusVal === 'approved'
              const isRevised = statusVal === 'revised'
              const isReject = statusVal === 'reject'
              const commentVal = noteDetails?.comment || existingNote?.comment

              if (isApproved) {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 2,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.05) 0%, rgba(76, 175, 80, 0.08) 100%)',
                      border: '1px solid rgba(46, 125, 50, 0.16)',
                      boxShadow: '0 2px 8px rgba(46, 125, 50, 0.04)',
                    }}
                  >
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                        Agency Approved
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                        This clinical note has been verified and approved by the partner agency.
                      </Typography>
                      {commentVal && (
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.primary', fontStyle: 'italic' }}>
                          Comment: {commentVal}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )
              }

              if (isRevised || isReject) {
                const colorScheme = isReject
                  ? { bg: 'rgba(211, 47, 47, 0.05)', border: 'rgba(211, 47, 47, 0.2)', text: 'error.dark', label: 'Agency Rejected Note' }
                  : { bg: 'rgba(237, 108, 2, 0.05)', border: 'rgba(237, 108, 2, 0.2)', text: 'warning.dark', label: 'Agency Requested Revision' }

                return (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: colorScheme.bg,
                      border: `1px solid ${colorScheme.border}`,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colorScheme.text }}>
                      {colorScheme.label}
                    </Typography>
                    {commentVal ? (
                      <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary' }}>
                        <strong>Agency Comment:</strong> {commentVal}
                      </Typography>
                    ) : null}
                  </Box>
                )
              }

              return null
            })()}

            {/* Read-only Patient Visibility Display */}
            {!hasAgencyId && isReadOnly && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Patient Visibility:{' '}
                  <strong>{patientVisible ? 'Visible' : 'Hidden'}</strong>
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {/* <Button
          onClick={onClose}
          disabled={savingDraft || finalizing || isDeleting}
          variant="outlined"
        >
          {isReadOnly ? 'Close' : 'Cancel'}
        </Button> */}
        {!isNewNote && noteId && session.status !== 'completed' && (
          <Button
            variant="outlined"
            color="error"
            startIcon={
              isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
            onClick={handleDeleteClick}
            disabled={savingDraft || finalizing || isDeleting}
          >
            Delete Note
          </Button>
        )}
        {!isReadOnly && (
          <>
          {hasAgencyId ? (
             <Button
              variant="outlined"
              startIcon={
                savingDraft ? <CircularProgress size={16} /> : <SaveIcon />
              }
              onClick={handleSaveDraft}
              disabled={
                savingDraft ||  finalizing || isDeleting || !noteContent.trim() || !signatureData
              }
            >
              
               {isNewNote ? 'Save & Submit for Agency Approval' : 'Update & Submit for Agency Approval'}
            </Button>
          ) : (
             <Button
              variant="outlined"
              startIcon={
                savingDraft ? <CircularProgress size={16} /> : <SaveIcon />
              }
              onClick={handleSaveDraft}
              disabled={
                savingDraft || finalizing || isDeleting || !noteContent.trim()
              }
            >
             {isNewNote ? 'Save Draft' : 'Update Draft'}
            </Button>
          )}
           
            {!hasAgencyId && (
              <Button
                variant="contained"
                startIcon={
                  finalizing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                onClick={handleFinalize}
                disabled={
                  savingDraft ||
                  finalizing ||
                  isDeleting ||
                  !noteContent.trim() ||
                  !signatureData
                }
                color="primary"
              >
                Finalize Note
              </Button>
            )}
          </>
        )}
        {isReadOnly && isFinalized && pdfUrl && (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
            disabled={savingDraft || finalizing || isDeleting}
            color="primary"
          >
            Download PDF
          </Button>
        )}
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'error.light',
                color: 'error.main',
              }}
            >
              <DeleteIcon />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Delete Note
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Typography variant="body1" color="text.primary" paragraph>
            Are you sure you want to delete this note? This action cannot be
            undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Once deleted, this note will be permanently removed and cannot be
              recovered.
            </Typography>
          </Alert>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={isDeleting}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: '0.5rem',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            variant="contained"
            color="error"
            startIcon={
              isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />
            }
            sx={{
              textTransform: 'none',
              borderRadius: '0.5rem',
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Note'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default NoteEditor
